// ---------- tiny formatting / decoding helpers shared across the app ----------

export const cn = (...xs) => xs.filter(Boolean).join(" ");

export function formatLovelace(value, mode) {
  if (value === null || value === undefined) return "—";
  const s = typeof value === "string" ? value : String(value);
  if (mode === "lovelace") return `${Number(s).toLocaleString("en-US")} L`;
  // convert to ADA (divide by 1e6) - keep 6 dp then trim
  const n = BigInt(s);
  const whole = n / 1000000n;
  const frac = (n % 1000000n).toString().padStart(6, "0").replace(/0+$/, "");
  const wholeStr = whole.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return frac ? `${wholeStr}.${frac} ₳` : `${wholeStr} ₳`;
}

export function truncMid(s, head = 8, tail = 6) {
  if (!s) return "—";
  if (s.length <= head + tail + 2) return s;
  return `${s.slice(0, head)}…${s.slice(-tail)}`;
}

export function unixToDate(ts) {
  if (!ts) return "—";
  const d = new Date(Number(ts) * 1000);
  return d.toISOString().replace("T", " ").slice(0, 19) + "Z";
}

export const formatRelativeTime = (unixSec) => {
  if (!unixSec) return "—";
  const diff = Math.max(0, Math.floor(Date.now() / 1000) - Number(unixSec));
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ${diff % 60}s ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ${Math.floor((diff % 3600) / 60)}m ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

// "2d 3h 14m" / "14m 22s" / "ended"
export const formatCountdown = (targetUnix) => {
  if (!targetUnix) return "—";
  const diff = Number(targetUnix) - Math.floor(Date.now() / 1000);
  if (diff <= 0) return "ended";
  const d = Math.floor(diff / 86400);
  const h = Math.floor((diff % 86400) / 3600);
  const m = Math.floor((diff % 3600) / 60);
  const s = diff % 60;
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  return `${m}m ${s}s`;
};

// duration between two unix timestamps → "5d 0h 0m"
export const formatDuration = (seconds) => {
  const diff = Math.max(0, Number(seconds) || 0);
  const d = Math.floor(diff / 86400);
  const h = Math.floor((diff % 86400) / 3600);
  const m = Math.floor((diff % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

// 5.87M / 1.24B / 9,182
export const formatBigCount = (v) => {
  if (v === null || v === undefined) return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  if (n >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(2)}K`;
  return n.toLocaleString("en-US");
};

// Big ADA amount from a lovelace string, abbreviated (e.g. "1.67B", "14.82M").
// Used by compact overview tiles where full precision isn't needed.
export const formatAdaBigCount = (lovelaceStr) => {
  if (lovelaceStr === null || lovelaceStr === undefined) return "—";
  const ada = Number(lovelaceStr) / 1e6;
  return formatBigCount(ada);
};

// Decodes a CIP-25 asset_name (hex) to a printable string. Used on the Asset
// summary panel. Strict: rejects anything that doesn't decode as valid UTF-8.
export const decodeAssetName = (hex) => {
  if (!hex) return "";
  try {
    const bytes = new Uint8Array(hex.match(/.{1,2}/g).map((h) => parseInt(h, 16)));
    const s = new TextDecoder("utf-8", { fatal: true }).decode(bytes);
    // accept printable ascii / unicode only
    if (/^[\x20-\x7E\u00A0-\uFFFF]+$/.test(s)) return s;
  } catch (_) {
    /* fall-through */
  }
  return "";
};

// Like decodeAssetName but also (a) strips a CIP-67/68 label prefix and
// (b) unwraps double hex-encoded names (decoded text that is itself even-length
// hex decoding again to a readable word, e.g. "676c6f62616c2d6661726d" ->
// "global-farm"). Shared by the dashboard and the asset detail page.
export const decodeAssetNameSmart = (hex) => {
  if (!hex) return "";
  let name = decodeAssetName(hex);
  if (!name && hex.length > 8 && /^00[0-9a-f]{6}/i.test(hex)) {
    name = decodeAssetName(hex.slice(8));
  }
  if (!name) return "";
  if (name.length >= 8 && name.length % 2 === 0 && /^[0-9a-f]+$/i.test(name)) {
    const inner = decodeAssetName(name);
    if (inner && /[a-z]/i.test(inner) && /^[\x20-\x7e]+$/.test(inner)) return inner;
  }
  return name;
};
// { policy, name }, best-effort decoding `name` as printable ASCII.
// Used by token-chip style renders (address token dropdown, UTXO rows, flow diagram).
export const _decodeAssetName = (unit) => {
  if (!unit || unit.length <= 56) return { policy: unit, name: "" };
  const policy = unit.slice(0, 56);
  const nameHex = unit.slice(56);
  let name = "";
  try {
    const bytes = nameHex.match(/.{1,2}/g)?.map((h) => parseInt(h, 16)) || [];
    name = String.fromCharCode(...bytes);
    if (!/^[\x20-\x7E]*$/.test(name)) name = `0x${nameHex.slice(0, 12)}`;
  } catch {
    name = `0x${nameHex.slice(0, 12)}`;
  }
  return { policy, name };
};

export const ipfsToHttp = (uri) => {
  if (!uri || typeof uri !== "string") return null;
  if (uri.startsWith("ipfs://")) return `https://ipfs.io/ipfs/${uri.slice(7)}`;
  if (uri.startsWith("http")) return uri;
  return null;
};
